import { briefDate } from './brief-date.ts';

/**
 * What the Brief says in Sigora's own words where somebody outside Sigora reads it — the PDF a
 * researcher downloads, and the read-only page a stakeholder is sent — and on the control that
 * downloads the PDF: one module, so the copy is one file to change. Everything else on those pages
 * is the stored Brief itself. Placeholder copy.
 */

/** Under the Verdict: which version this is, and that nothing on it can be changed. */
export const readOnlyVersionLine = (version: number): string =>
  `Version ${String(version)}. Read-only.`;

/** How many follow-up questions, said in words that agree with the number. */
const followUpQuestions = (count: number): string =>
  `${String(count)} follow-up question${count === 1 ? '' : 's'}`;

/**
 * Under the Verdict: who the Brief was prepared for, the version and the day it was written, how
 * many follow-up questions it has answered (said only once there is one), and who holds the
 * original of what the researcher framed.
 */
export const provenanceLine = ({
  ownerName,
  version,
  versionCreatedAt,
  followUpCount,
}: {
  ownerName: string;
  version: number;
  versionCreatedAt: string;
  followUpCount: number;
}): string => {
  const after = followUpCount === 0 ? '' : ` after ${followUpQuestions(followUpCount)}`;

  return `Prepared with Sigora for ${ownerName}. Version ${String(version)}, updated ${briefDate(versionCreatedAt)}${after}. Edited blocks are marked; the original is available from ${ownerName}.`;
};

/** Before `SIMULATED_LINE` on the banner a simulated Brief opens on. */
export const SIMULATED_BANNER_LABEL = 'Simulated participants.';

/** In every page's footer of a simulated Brief. */
export const SIMULATED_FOOTER = 'Simulated participants';

/** The Verdict has no heading of its own; a page it runs onto is headed with this. */
export const VERDICT_HEADING = 'Verdict';

/** At the top of a page a block runs onto. */
export const continuedHeading = (heading: string): string => `${heading} (continued)`;

/** What the print page says when it was given nothing to print. */
export const NOTHING_TO_PRINT = 'Nothing to print.';

/** The time a snapshot was taken, in UTC and the 24-hour clock, so two exports a day apart read apart. */
const EXPORT_TIME = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
});

/**
 * The left of every page's footer: the version and the day it was written, when the snapshot was
 * taken, and on a simulated Brief that its participants were simulated.
 */
export const footerLeft = ({
  version,
  versionCreatedAt,
  capturedAt,
  simulated,
}: {
  version: number;
  versionCreatedAt: string;
  capturedAt: string;
  simulated: boolean;
}): string => {
  const exported = `exported ${briefDate(capturedAt)}, ${EXPORT_TIME.format(new Date(capturedAt))} UTC`;
  const parts = [`Version ${String(version)}, updated ${briefDate(versionCreatedAt)}`, exported];

  return (simulated ? [...parts, SIMULATED_FOOTER] : parts).join(' · ');
};

/**
 * The right of every page's footer. `{page}` and `{pages}` stand for the page's number and how many
 * there are, which only the printer knows: it puts them in.
 */
export const FOOTER_RIGHT = 'Page {page} of {pages}';

/** The control on the Brief that downloads the current version as a PDF. */
export const DOWNLOAD_PDF_LABEL = 'Download PDF';

/** While the PDF is being made. */
export const BRIEF_EXPORT_PENDING = 'Preparing the PDF…';

/** Once the file is handed to the browser: the version it holds, which the server names. */
export const briefExportDone = (version: number): string =>
  `Downloaded version ${String(version)}.`;

/** When no PDF came back, for any reason another try may get past. */
export const BRIEF_EXPORT_FAILED = "The PDF couldn't be made.";

/** When the server is making as many PDFs as it will: Try again waits until it says to ask. */
export const BRIEF_EXPORT_BUSY = 'Sigora is making other PDFs right now. Try again in a moment.';

/** Asks for the PDF again after it could not be made. */
export const BRIEF_EXPORT_TRY_AGAIN = 'Try again';

/** When the study has no published Brief to make a PDF of. */
export const BRIEF_EXPORT_NOT_PUBLISHED = 'This study has no brief yet.';
